// C11 — Notifications + Shared-with-me API client.
//
// Wraps the C7 endpoints (notification inbox) and one C11 endpoint
// (GET /api/me/shared-resources). Same error-unwrap convention as
// collabApi / invitationApi.

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const DESIGNER_TOKEN_KEY = 'user_token';

const getToken = () =>
  localStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(DESIGNER_TOKEN_KEY);

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

function unwrapError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    const e = new Error(detail.message || 'Request failed');
    e.code = detail.code;
    e.status = status;
    e.detail = detail;
    return e;
  }
  // String detail → map by HTTP status so callers get friendly copy
  // instead of "Request failed with status code 401".
  const friendly = friendlyByStatus(status);
  const message = friendly?.message
    || (typeof detail === 'string' ? detail : err?.message)
    || 'Request failed';
  const e = new Error(message);
  e.code = friendly?.code || null;
  e.status = status;
  return e;
}

function friendlyByStatus(status) {
  switch (status) {
    case 401: return { code: 'session_expired',
                       message: 'Your session has expired. Please sign in again.' };
    case 403: return { code: 'forbidden',
                       message: "You don't have permission to do that." };
    case 429: return { code: 'too_many_requests',
                       message: 'Too many requests — please slow down.' };
    case 500: case 502: case 503: case 504:
      return { code: 'server_error',
               message: "Something went wrong on our end. Try again in a moment." };
    default: return null;
  }
}

async function call(method, url, body) {
  try {
    const res = await axios.request({
      method, url, data: body, headers: authHeaders(),
    });
    return res.data;
  } catch (err) {
    throw unwrapError(err);
  }
}

const qs = (params = {}) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!entries.length) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
};

export const notificationsApi = {
  // Paginated inbox. params: { cursor, limit, category, unread_only, include_archived }
  list: (params = {}) =>
    call('get', `${API_URL}/api/notifications/me${qs(params)}`),

  unreadCount: (params = {}) =>
    call('get', `${API_URL}/api/notifications/me/unread-count${qs(params)}`),

  markRead: (id) =>
    call('post', `${API_URL}/api/notifications/${id}/read`),

  markAllRead: (body = {}) =>
    call('post', `${API_URL}/api/notifications/me/read-all`, body),

  archive: (id) =>
    call('post', `${API_URL}/api/notifications/${id}/archive`),

  unarchive: (id) =>
    call('post', `${API_URL}/api/notifications/${id}/unarchive`),
};

export const sharedApi = {
  // C6 endpoint — pending invitations
  pendingInvitations: () =>
    call('get', `${API_URL}/api/invitations/me`),

  // C11 endpoint — accepted shared resources
  sharedResources: () =>
    call('get', `${API_URL}/api/me/shared-resources`),
};

// ── Relative-time helper used by NotificationItem ────────────────────────
export function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const week = Math.floor(day / 7);
  if (week < 4) return `${week}w ago`;
  const mon = Math.floor(day / 30);
  if (mon < 12) return `${mon}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}
